/* eslint-disable jsdoc/check-param-names, jsdoc/require-param, tsdoc/syntax */
import {useEffect} from 'react'
import useTimeoutFn from 'react-use/lib/useTimeoutFn'

import {useDocumentVisibility} from './useDocumentVisibility'
import {useWindowFocus} from './useWindowFocus'

/**
 * Hook that executes a callback after a specified timeout when the window regains focus.
 *
 * The timer starts when the component mounts. When the timer expires, the callback will be
 * executed the next time the window becomes focused and visible. After execution, the timer
 * resets and the cycle continues.
 * @param options - Configuration options
 * @param options.callbackTimeoutMs - Time in milliseconds before the callback becomes eligible to execute
 * @param options.callback - Function to execute when the window regains focus after the timeout
 * @returns A function that restarts the timer
 */
export const useActiveWindowTimeout = ({
  callbackTimeoutMs,
  callback
}: {
  callbackTimeoutMs: number
  callback: () => void
}) => {
  const isFocused = useWindowFocus()
  const isVisible = useDocumentVisibility()

  const isActiveWindow = isFocused && isVisible

  const [isReady, , reset] = useTimeoutFn(() => {
    if (!isActiveWindow) return
    reset()
    callback()
  }, callbackTimeoutMs)

  useEffect(() => {
    if (isActiveWindow && isReady()) {
      reset()
      callback()
    }
  }, [isActiveWindow, isReady, reset, callback])

  return reset
}
